import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * RelatedContent — fetches and renders cross-links between posts and lessons.
 *
 * Props:
 *   kind: 'post' (showing related lessons) | 'lesson' (showing related posts)
 *   slug: current item's slug
 */
const RelatedContent = ({ kind, slug }) => {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const url = kind === 'post'
          ? `${API_URL}/api/posts/${slug}/related-lessons`
          : `${API_URL}/api/lessons/${slug}/related-posts`;
        const res = await axios.get(url);
        const data = kind === 'post' ? (res.data.lessons || []) : (res.data.posts || []);
        if (!cancelled) setItems(data);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [kind, slug]);

  if (!loaded || items.length === 0) return null;

  if (kind === 'post') {
    return (
      <section className="mt-16 pt-12 border-t border-gray-200" data-testid="related-lessons">
        <div className="flex items-center justify-between mb-8">
          <h3 className="text-2xl font-light flex items-center gap-3">
            <BookOpen size={22} className="text-indigo-600" />
            Bite-sized lessons on this
          </h3>
          <Link to="/learn" className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1">
            All lessons <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((l) => (
            <Link
              key={l.slug}
              to={`/learn/${l.slug}`}
              className="group p-5 rounded-xl border border-gray-200 bg-white hover:border-gray-400 hover:shadow-md transition"
              data-testid={`related-lesson-${l.slug}`}
            >
              <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-indigo-700 bg-indigo-50 rounded-full px-2.5 py-0.5 mb-3">
                {l.topic}
              </span>
              <h4 className="text-base font-medium text-gray-900 mb-2 leading-snug group-hover:text-black">
                {l.title}
              </h4>
              <p className="text-sm text-gray-600 line-clamp-3">{l.summary}</p>
              <span className="inline-flex items-center gap-1 text-xs text-gray-500 mt-3">{l.readTime}</span>
            </Link>
          ))}
        </div>
      </section>
    );
  }

  // kind === 'lesson' → show related posts
  return (
    <section className="mt-16 pt-12 border-t border-gray-200" data-testid="related-posts">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-light flex items-center gap-3">
          <FileText size={22} className="text-emerald-600" />
          Deep-dive articles on this
        </h3>
        <Link to="/archive" className="text-sm text-gray-600 hover:text-black inline-flex items-center gap-1">
          All articles <ArrowRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {items.map((p) => (
          <Link
            key={p.slug}
            to={`/post/${p.slug}`}
            className="group rounded-xl border border-gray-200 bg-white overflow-hidden hover:border-gray-400 hover:shadow-md transition"
            data-testid={`related-post-${p.slug}`}
          >
            {p.image ? (
              <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            ) : null}
            <div className="p-5">
              <span className="inline-block text-[11px] font-semibold uppercase tracking-wider text-gray-600 mb-2">
                {p.category}
              </span>
              <h4 className="text-base font-medium text-gray-900 mb-2 leading-snug group-hover:text-black">{p.title}</h4>
              <p className="text-sm text-gray-600 line-clamp-2">{p.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedContent;
